import React, { useMemo, useEffect } from 'react';
import { X, History, MoreVertical, Loader2, CheckCircle, Columns, Eye, Edit3, RotateCcw } from 'lucide-react';
import { formatLocalDateTime } from '../../../../utils/date';

const VersionHistorySidebar = ({
    showVersionSidebar,
    setShowVersionSidebar,
    versionsList,
    activeSection,
    stripPrefix,
    activeVersionMenuId,
    setActiveVersionMenuId,
    handleViewVersion,
    isFetchingVersionDetails,
    setLabelTargetVersion,
    setVersionLabelText,
    setShowLabelModal,
    setRevertTargetVersion,
    setShowRevertModal,
    setIsSelectingCompareFromView,
    isSelectingCompareFromView,
    compareSourceVersion,
    setCompareSourceVersion,
    fetchComparison,
    setSelectedVersionData,
    setShowVersionDetailsModal,
    isFetchingComparison,
    isManager = false,
    currentSection = null,
    handleApproveVersion = null,
    isApprovingVersion = false,
    approvingVersionNum = null
}) => {
    // Close dropdown on click outside
    useEffect(() => {
        if (!activeVersionMenuId) return;
        const handleClickOutside = (e) => {
            if (!e.target.closest('.v-options-wrapper')) {
                setActiveVersionMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeVersionMenuId, setActiveVersionMenuId]);
    // Determine section status accurately
    const rawStatusCode = currentSection?.status?.code ?? currentSection?.status_code ?? currentSection?.statusCode;
    const currentStatusStr = String(currentSection?.status?.label || currentSection?.status || '').toLowerCase();
    const currentStatusCode = Number(rawStatusCode) || (
        currentStatusStr.includes('submitted') && !currentStatusStr.includes('not submitted') ? 53 :
        currentStatusStr.includes('approved') ? 54 :
        currentStatusStr.includes('revision') || currentStatusStr.includes('needs revision') ? 55 :
        currentStatusStr.includes('progress') ? 52 :
        0
    );
    const isSubmitted = currentStatusCode === 53;
    const isApproved = currentStatusCode === 54;

    // Find newest TL Approved version number to show "Live on main document" badge
    const newestTLApprovedVersionNumber = useMemo(() => {
        if (!Array.isArray(versionsList) || versionsList.length === 0) return null;
        let maxVer = null;
        for (const v of versionsList) {
            const isTL = v.change_type === 'tl_approved' || 
                         (v.change_type && String(v.change_type).toLowerCase() === 'tl_approved') ||
                         (v.change_type_label && String(v.change_type_label).toLowerCase().includes('tl approved'));
            if (isTL) {
                const num = Number(v.version_number ?? v.version_id ?? v.id);
                if (maxVer === null || num > maxVer) {
                    maxVer = num;
                }
            }
        }
        return maxVer;
    }, [versionsList]);

    // Find the latest version number
    const latestVersionNumber = useMemo(() => {
        if (!Array.isArray(versionsList) || versionsList.length === 0) return null;
        return Math.max(...versionsList.map(v => Number(v.version_number ?? v.version_id ?? v.id) || 0));
    }, [versionsList]);

    const formatChangeLabel = (ver) => {
        if (ver.change_type_label) return ver.change_type_label;
        const type = String(ver.change_type || '').toLowerCase();
        if (type === 'tl_approved' || type === 'tl-approved') return 'TL Approved';
        if (type === 'live_edit_auto_save' || type === 'auto_save' || type === 'live_edit') return 'Live Edit Auto-Save';
        if (type === 'automatic_draft' || type === 'auto_draft') return 'Automatic Draft';
        if (type === 'restored' || type === 'revert' || type === 'restored_version') return 'Restored Version';
        if (type === 'manual_edit' || type === 'manual') return 'Manual Edit';
        return ver.change_type || 'Manual Edit';
    };

    return (
        <div className={`version-sidebar-overlay ${showVersionSidebar ? 'show' : ''}`} onClick={() => {
            setShowVersionSidebar(false);
            setIsSelectingCompareFromView(false);
            setCompareSourceVersion(null);
        }}>
            <div className={`version-sidebar-content ${showVersionSidebar ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="version-sidebar-header">
                    {!isSelectingCompareFromView ? (
                        <>
                            <div className="version-title-group">
                                <h4>Version History</h4>
                                <p>{stripPrefix(activeSection)} · {Array.isArray(versionsList) ? versionsList.length : 0} versions</p>
                            </div>
                            <button className="version-close-btn" onClick={() => setShowVersionSidebar(false)}>
                                <X size={20} />
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="version-title-group selection-mode">
                                <h4>Compare with...</h4>
                                <p>Select another version to compare with V{compareSourceVersion?.version_number}</p>
                            </div>
                            <button className="v-cancel-selection-btn" onClick={() => {
                                setIsSelectingCompareFromView(false);
                                setCompareSourceVersion(null);
                            }}>Cancel</button>
                        </>
                    )}
                </div>

                {/* Quick Compare Live vs Latest default comparison */}
                {!isSelectingCompareFromView && newestTLApprovedVersionNumber && latestVersionNumber && newestTLApprovedVersionNumber !== latestVersionNumber && (
                    <div className="v-quick-compare-bar" style={{ padding: '8px 16px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                        <button
                            className="v-quick-compare-btn"
                            onClick={() => fetchComparison(newestTLApprovedVersionNumber, latestVersionNumber)}
                            disabled={isFetchingComparison}
                            title={`Compare Live Version ${newestTLApprovedVersionNumber} with Latest Version ${latestVersionNumber}`}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                background: '#FFFFFF',
                                border: '1px solid #CBD5E1',
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#1E293B',
                                cursor: isFetchingComparison ? 'wait' : 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {isFetchingComparison ? <Loader2 size={13} className="animate-spin" /> : <Columns size={13} color="#FF5526" />}
                            <span>Compare Live (v{newestTLApprovedVersionNumber}) vs Latest (v{latestVersionNumber})</span>
                        </button>
                    </div>
                )}

                <div className="version-list-container">
                    {!Array.isArray(versionsList) || versionsList.length === 0 ? (
                        <div className="no-versions-state">
                            <History size={32} />
                            <p>No versions available for this section.</p>
                        </div>
                    ) : (() => {
                        const parseUTCDate = (timestamp) => {
                            if (!timestamp) return new Date();
                            let normalized = timestamp;
                            if (typeof timestamp === 'string' && timestamp.includes('T') && !timestamp.endsWith('Z') && !/\+\d{2}:?\d{2}$/.test(timestamp) && !/-\d{2}:?\d{2}$/.test(timestamp)) {
                                normalized = `${timestamp}Z`;
                            }
                            return new Date(normalized);
                        };

                        const groups = {};
                        versionsList.forEach(v => {
                            const date = parseUTCDate(v.created_at);
                            const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                            const today = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                            const yesterday = new Date(Date.now() - 86400000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

                            let label = dateStr;
                            if (dateStr === today) label = 'TODAY';
                            else if (dateStr === yesterday) label = 'YESTERDAY';

                            if (!groups[label]) groups[label] = [];
                            groups[label].push(v);
                        });

                        return Object.entries(groups).map(([label, versions]) => (
                            <div key={label} className="version-date-group">
                                <div className="version-date-label">{label}</div>
                                {versions.map((v, idx) => {
                                    const authorName = v.created_by_name || 'System';
                                    const authorList = authorName.includes(',')
                                        ? authorName.split(',').map(s => s.trim()).filter(Boolean)
                                        : [authorName];
                                    const displayTime = formatLocalDateTime(v.created_at);
                                    const vNum = Number(v.version_number ?? v.version_id ?? v.id);
                                    const isTLApproved = v.change_type === 'tl_approved' || (v.change_type && String(v.change_type).toLowerCase() === 'tl_approved');
                                    const isLiveOnMain = vNum === newestTLApprovedVersionNumber;
                                    const isFormalSubmission = Boolean(
                                        (v.comment && String(v.comment).toLowerCase().includes('formal submission')) ||
                                        (v.reason && String(v.reason).toLowerCase().includes('formal submission')) ||
                                        (v.label && String(v.label).toLowerCase().includes('formal submission'))
                                    );
                                    const typeLabel = formatChangeLabel(v);
                                    const isSource = compareSourceVersion && (v.version_number === compareSourceVersion.version_number);
                                    const menuId = String(v.version_number != null ? v.version_number : (v.version_id || v.id || `${label}-${idx}`));
                                    const isMenuOpen = activeVersionMenuId === menuId;

                                    return (
                                        <div
                                            key={menuId}
                                            className={`version-item ${idx === 0 && label === 'TODAY' && !isSelectingCompareFromView ? 'active' : ''} ${isSource ? 'source-version' : ''} ${isSelectingCompareFromView ? 'selection-active' : ''}`}
                                            onClick={() => {
                                                if (isSelectingCompareFromView) {
                                                    if (isSource) return;
                                                    fetchComparison(compareSourceVersion.version_number, v.version_number);
                                                    setIsSelectingCompareFromView(false);
                                                    setCompareSourceVersion(null);
                                                } else {
                                                    !isFetchingVersionDetails && handleViewVersion(v);
                                                }
                                            }}
                                            style={{ cursor: (isFetchingVersionDetails || isFetchingComparison) ? 'wait' : 'pointer' }}
                                        >
                                            <div className="version-item-header">
                                                <div className="v-number-row">
                                                    <span className="v-number">{v.label || `Version ${v.version_number}`}</span>
                                                    {isFormalSubmission && (
                                                        <span className="v-submission-badge" title="Employee Formal Submission">
                                                            Formal submission
                                                        </span>
                                                    )}
                                                    {isLiveOnMain && (
                                                        <span className="v-live-badge" title="This approved version is currently live on the main RFP document">
                                                            <span className="v-live-dot" />
                                                            Live on main document
                                                        </span>
                                                    )}
                                                </div>
                                                {!isSelectingCompareFromView && (
                                                    <div 
                                                        className={`v-options-wrapper ${isMenuOpen ? 'active' : ''}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <span className={`v-badge ${(v.change_type || 'manual').toLowerCase()}`}>
                                                            {isTLApproved && <CheckCircle size={10} style={{ marginRight: '3px', display: 'inline', verticalAlign: 'middle' }} />}
                                                            {typeLabel}
                                                        </span>
                                                        <button
                                                            className="v-options-btn"
                                                            title="More options"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveVersionMenuId(isMenuOpen ? null : menuId);
                                                            }}
                                                        >
                                                            <MoreVertical size={14} />
                                                        </button>
                                                        {isMenuOpen && (
                                                            <div className="v-dropdown-menu">
                                                                {isManager && !isApproved && (
                                                                    <button
                                                                        className={`v-dropdown-item v-dropdown-approve-item ${isSubmitted ? 'ready' : 'disabled'}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            if (!isSubmitted) return;
                                                                            if (handleApproveVersion) handleApproveVersion(v.version_number);
                                                                            setActiveVersionMenuId(null);
                                                                        }}
                                                                        disabled={!isSubmitted || (isApprovingVersion && approvingVersionNum === v.version_number)}
                                                                    >
                                                                        {isApprovingVersion && approvingVersionNum === v.version_number ? (
                                                                            <Loader2 size={13} className="animate-spin" />
                                                                        ) : (
                                                                            <CheckCircle size={13} color={isSubmitted ? '#15803D' : '#94A3B8'} />
                                                                        )}
                                                                        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.25 }}>
                                                                            <span style={{ fontWeight: 600, color: isSubmitted ? '#15803D' : '#64748B' }}>Approve this version</span>
                                                                            {!isSubmitted && (
                                                                                <span style={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 400, marginTop: '2px' }}>
                                                                                    (Requires submit)
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                    </button>
                                                                )}
                                                                <button 
                                                                    className="v-dropdown-item"
                                                                    onClick={(e) => { 
                                                                        e.stopPropagation(); 
                                                                        setActiveVersionMenuId(null);
                                                                        handleViewVersion(v); 
                                                                    }} 
                                                                    disabled={isFetchingVersionDetails && activeVersionMenuId === menuId}
                                                                >
                                                                    {isFetchingVersionDetails && activeVersionMenuId === menuId ? (
                                                                        <Loader2 className="animate-spin" size={13} />
                                                                    ) : (
                                                                        <Eye size={13} />
                                                                    )}
                                                                    <span>View</span>
                                                                </button>
                                                                <button 
                                                                    className="v-dropdown-item"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setLabelTargetVersion(v);
                                                                        setVersionLabelText(v.label || '');
                                                                        setShowLabelModal(true);
                                                                        setActiveVersionMenuId(null);
                                                                    }}
                                                                >
                                                                    <Edit3 size={13} />
                                                                    <span>Rename</span>
                                                                </button>
                                                                <button 
                                                                    className="v-dropdown-item"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setRevertTargetVersion(v);
                                                                        setShowRevertModal(true);
                                                                        setActiveVersionMenuId(null);
                                                                    }}
                                                                >
                                                                    <RotateCcw size={13} />
                                                                    <span>Revert</span>
                                                                </button>
                                                                <button 
                                                                    className="v-dropdown-item"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setIsSelectingCompareFromView(true);
                                                                        setCompareSourceVersion(v);
                                                                        setActiveVersionMenuId(null);
                                                                    }} 
                                                                    disabled={isFetchingComparison}
                                                                >
                                                                    {isFetchingComparison && activeVersionMenuId === menuId ? (
                                                                        <Loader2 className="animate-spin" size={13} />
                                                                    ) : (
                                                                        <Columns size={13} />
                                                                    )}
                                                                    <span>Compare</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {isSelectingCompareFromView && isSource && (
                                                    <span className="source-indicator">Selected</span>
                                                )}
                                            </div>
                                            <div className="v-meta">
                                                <div className="v-author-avatar-group" style={{ display: 'flex', alignItems: 'center' }}>
                                                    {authorList.slice(0, 2).map((name, i) => (
                                                        <div
                                                            key={i}
                                                            className="v-author-avatar"
                                                            title={name}
                                                            style={{
                                                                marginLeft: i > 0 ? '-6px' : '0',
                                                                zIndex: 2 - i,
                                                                border: '1.5px solid #FFFFFF'
                                                            }}
                                                        >
                                                            {name[0]?.toUpperCase() || 'U'}
                                                        </div>
                                                    ))}
                                                </div>
                                                <span title={authorName}>{authorName} · {displayTime}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ));
                    })()}
                </div>
            </div>
        </div>
    );
};

export default VersionHistorySidebar;
